import { apiBaseUrl } from '../config/env';

const API_BASE = apiBaseUrl;

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export function sanitizeErrorMessage(rawMessage: string, status?: number): string {
  if (!rawMessage) {
    return status === 500
      ? 'خطای غیرمنتظره‌ای در سرور رخ داده است. لطفاً پس از چند لحظه دوباره امتحان کنید.'
      : 'درخواست با خطا مواجه شد. لطفاً مجدداً تلاش کنید.';
  }

  const lower = rawMessage.toLowerCase();

  // MongoDB duplicate key errors
  if (lower.includes('e11000') || lower.includes('duplicate key')) {
    if (lower.includes('email') || lower.includes('phone') || lower.includes('موبایل') || lower.includes('ایمیل')) {
      return 'حسابی با این ایمیل یا شماره موبایل از قبل وجود دارد. لطفاً وارد شوید یا از بازیابی رمز عبور استفاده کنید.';
    }
    return 'این اطلاعات قبلاً در سامانه ثبت شده است و تکراری می‌باشد.';
  }

  // Database connection / buffering / timeout errors
  if (
    lower.includes('buffering timed out') ||
    lower.includes('findone()') ||
    lower.includes('mongodb') ||
    lower.includes('mongoservererror') ||
    lower.includes('mongoose') ||
    lower.includes('econnrefused')
  ) {
    return 'خطا در برقراری ارتباط با سامانه. لطفاً پس از چند لحظه دوباره امتحان کنید.';
  }

  // SMS template errors from gateway
  if (rawMessage.includes('قالب یافت نشد') || lower.includes('template not found')) {
    return 'ارسال پیامک با خطا مواجه شد (قالب پیامک در سامانه پیامکی تأیید نشده است). لطفاً از طریق تب ایمیل اقدام کنید یا با پشتیبانی تماس بگیرید.';
  }

  // SMTP / Mail provider technical leaks
  if (
    lower.includes('invalid account') ||
    lower.includes('domain not found') ||
    lower.includes('smtp') ||
    lower.includes('tenant')
  ) {
    return 'ارسال ایمیل موقتاً با خطا مواجه شد. لطفاً از تأیید با پیامک استفاده کنید یا با پشتیبانی تماس بگیرید.';
  }

  // Raw 500 error default
  if (status === 500 && (lower.includes('internal server error') || rawMessage.trim() === 'Internal Server Error')) {
    return 'خطایی در پردازش سرور رخ داده است. لطفاً مجدداً تلاش کنید.';
  }

  return rawMessage;
}

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('accessToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
      ...options.headers,
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const raw = await response.text();
    let message = response.statusText;
    if (raw) {
      try {
        const body = JSON.parse(raw) as { message?: string };
        message = body.message ?? raw;
      } catch {
        message = raw;
      }
    }
    const sanitized = sanitizeErrorMessage(message, response.status);
    throw new ApiError(response.status, sanitized || 'Request failed');
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  upload: async <T>(path: string, form: FormData): Promise<T> => {
    const response = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: form,
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({ message: response.statusText }));
      const msg = sanitizeErrorMessage(body.message ?? 'Upload failed', response.status);
      throw new ApiError(response.status, msg);
    }
    return response.json();
  },
  getBlob: async (path: string): Promise<string> => {
    const response = await fetch(`${API_BASE}${path}`, {
      headers: getAuthHeaders(),
      credentials: 'include',
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({ message: response.statusText }));
      const msg = sanitizeErrorMessage(body.message ?? 'Request failed', response.status);
      throw new ApiError(response.status, msg);
    }
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  },
};

export { ApiError };
